import { useEffect, useState } from "react";
import { RefreshCw, AlertTriangle, Landmark, Settings2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { FxBudgetRateDialog } from "@/components/FxBudgetRateDialog";
import { api, errorText } from "@/lib/api";
import { fmtIdr, fmtRate } from "@/lib/format";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "-");

export const FxRatesCard = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === "super_admin";
  const [fx, setFx] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [dlg, setDlg] = useState(false);
  const load = () => api.get("/fx/latest").then((r) => { setFx(r.data); setErr(""); }).catch((e) => setErr(errorText(e)));
  useEffect(() => { load(); }, []);

  const refresh = async () => {
    setBusy(true);
    try {
      const r = await api.post("/fx/refresh");
      toast.success(`Rates updated — ECB date ${r.data.rate_date}`);
      await load();
    } catch (e) { toast.error(errorText(e)); } finally { setBusy(false); }
  };

  if (err && !fx) return <p className="mt-8 text-xs text-slate-500" data-testid="fx-card-error">Exchange rates unavailable: {err}</p>;
  if (!fx) return null;
  const idr = fx.rates?.IDR;
  const years = Object.keys(fx.budget_rates || {}).sort();
  const nextYear = new Date().getFullYear() + 1;
  const inUseYear = years.includes(String(nextYear)) ? String(nextYear) : years[years.length - 1];
  const inUse = inUseYear ? fx.budget_rates[inUseYear] : null;
  const others = (fx.targets || []).filter((c) => c !== "IDR");

  return (
    <section className="mt-8 bg-white border border-slate-200 rounded-lg p-5 sm:p-6 rise rise-1" data-testid="fx-rates-card">
      <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-5">
        <div className="flex gap-4">
          <div className="h-11 w-11 rounded-md bg-slate-900 text-emerald-300 grid place-items-center shrink-0"><Landmark size={20} /></div>
          <div>
            <p className="text-[11px] tracking-[0.25em] uppercase text-slate-500 font-semibold">Exchange rate · macro factor</p>
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 mt-1">
              <span className="font-heading text-3xl font-bold text-slate-900 tabular-nums" data-testid="fx-usd-idr">{idr ? fmtIdr(idr) : "-"}</span>
              <span className="text-sm text-slate-500">per 1 USD</span>
            </div>
            <p className="text-xs text-slate-500 mt-1">
              Rate date <span className="font-medium text-slate-700" data-testid="fx-rate-date">{fmtDate(fx.rate_date)}</span> · Source <span className="font-medium text-slate-700" data-testid="fx-source">{fx.source}</span>
            </p>
            {fx.stale && (
              <p className="mt-2 inline-flex items-center gap-1.5 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1" data-testid="fx-stale-warning">
                <AlertTriangle size={12} /> Live rate service unavailable — showing last stored rate from {fmtDate(fx.rate_date)}.
              </p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-5 gap-2 sm:gap-3" data-testid="fx-other-rates">
          {others.map((c) => (
            <div key={c} className="rounded border border-slate-100 bg-slate-50 px-2.5 py-2 min-w-[72px]" data-testid={`fx-rate-${c}`}>
              <p className="text-[10px] uppercase tracking-wider text-slate-500">USD/{c}</p>
              <p className="font-mono text-sm text-slate-900 mt-0.5">{fmtRate(fx.rates?.[c], c)}</p>
            </div>
          ))}
        </div>

        <div className="lg:text-right border-t lg:border-t-0 lg:border-l border-slate-100 pt-4 lg:pt-0 lg:pl-5 min-w-[220px]">
          <p className="text-[11px] tracking-[0.25em] uppercase text-slate-500 font-semibold">Budget FX rate in use</p>
          {inUse ? (
            <>
              <p className="font-heading text-2xl font-bold text-emerald-700 tabular-nums mt-1" data-testid="fx-budget-rate">{fmtIdr(inUse.rate)}</p>
              <p className="text-xs text-slate-500" data-testid="fx-budget-rate-year">Budget {inUseYear}{inUse.note ? ` · ${inUse.note}` : ""}</p>
              {idr && <p className="text-[11px] text-slate-500 mt-0.5" data-testid="fx-budget-vs-live">{((inUse.rate / idr - 1) * 100).toFixed(2)}% vs live</p>}
            </>
          ) : (
            <p className="text-sm text-slate-500 mt-1" data-testid="fx-budget-rate-missing">Not set — live rate applies.</p>
          )}
          {years.length > 1 && <p className="text-[11px] text-slate-400 mt-1" data-testid="fx-budget-rate-years">{years.map((y) => `${y}: ${fmtIdr(fx.budget_rates[y].rate)}`).join(" · ")}</p>}
          {isAdmin && (
            <div className="flex flex-wrap lg:justify-end gap-2 mt-3">
              <Button type="button" variant="outline" size="sm" onClick={refresh} disabled={busy} data-testid="fx-refresh-button" className="h-8 border-slate-300"><RefreshCw size={13} className={`mr-1.5 ${busy ? "animate-spin" : ""}`} /> Refresh rates</Button>
              <Button type="button" size="sm" onClick={() => setDlg(true)} data-testid="fx-set-budget-rate-button" className="h-8 bg-slate-900 hover:bg-emerald-700 text-white"><Settings2 size={13} className="mr-1.5" /> Set budget rate</Button>
            </div>
          )}
        </div>
      </div>
      {isAdmin && <FxBudgetRateDialog open={dlg} onOpenChange={setDlg} budgetRates={fx.budget_rates} defaultYear={nextYear} onSaved={load} />}
    </section>
  );
};
